import { ReactNode, useState, createContext, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '@demind-inc/core';
import * as Sentry from '@sentry/react';

import { useSignIn, useUpdateUser } from '../mutations';
import { Status } from '../types';
import {
  accountApi,
  getOAuthAccessToken,
  getOAuthRefreshToken,
  setOAuthAccessToken,
  setOAuthRefreshToken,
} from '../api';
import { useUser } from '../queries';
import { transformExpiryDate } from '../helpers';
import { isDesktopApp, trackEventMixpanel } from '../../utils';
import { safeLocalStorage } from '../../utils/safeLocalStorage';

// Must match STORAGE_KEY in components/walkthrough/WalkthroughController.tsx.
const PRESERVED_ON_SIGN_OUT_KEYS = ['walkthrough_complete'] as const;
import Cookies from 'js-cookie';
import { useRecoilState } from 'recoil';
import { generalSnackBarAtom } from '../recoil';
import { initiateAppleSignIn } from '../initializer/appleAuth';
import { captureGoogleAuthError, googleAuth } from '../initializer';

interface IAuthContext {
  status: Status;
  user: User;
  isUserFetching: boolean;
  isUpdatingUser?: boolean;
  signIn: (provider?: AuthProviderName) => Promise<User>;
  signOut: () => Promise<void>;
  updateUser: (user: Partial<User>, userId?: string) => Promise<void>;
}

type AuthProviderName = 'google' | 'apple';

// Context
export const AuthContext = createContext({} as IAuthContext);
export const useAuthContext = () => useContext(AuthContext);
export const gCalScopes =
  'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.readonly';
const infiniteDays = 365 * 100;
const DESKTOP_GOOGLE_SIGNIN_SCOPES = ['openid', 'email', 'profile'];

// Provider
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<Status>(Status.LOADING);
  const [userId, setUserId] = useState<string>('');
  const [_, setGeneralSnackbar] = useRecoilState(generalSnackBarAtom);
  const { user, isFetching: isUserFetching } = useUser({ userId });
  const { updateUser: updateUserMutation, isPending: isUpdatingUser } = useUpdateUser();
  const { signIn: signInMutation } = useSignIn();

  useEffect(() => {
    const authenticate = async () => {
      setStatus(Status.LOADING);
      const localUserId = safeLocalStorage.getItem('lifestack_user_id');
      const localRefresh = getOAuthRefreshToken();
      const localAccess = getOAuthAccessToken();
      const jwtConfigured = safeLocalStorage.getItem('jwt_configured');

      if (localUserId && localRefresh && localAccess) {
        // Introduce the JWT flow if it's not configured
        //TODO: Remove this after all user migrates to JWT flow
        if (!jwtConfigured || jwtConfigured === 'false') {
          const { accessToken, refreshToken } = (await accountApi.generateJwt(localUserId)).data;
          setOAuthRefreshToken(refreshToken);
          setOAuthAccessToken(accessToken);
          safeLocalStorage.setItem('jwt_configured', 'true');
        }

        setUserId(localUserId);
        setStatus(Status.AUTHORIZED);

        // Store them in cookies to share the login-state with the chrome extension
        Cookies.set('lifestack_user_id', localUserId!, { expires: infiniteDays });
        Cookies.set('lifestack_refresh_token', localRefresh!, { expires: infiniteDays });
        Cookies.set('lifestack_access_token', localAccess!, { expires: 30 });
        return;
      }

      setStatus(Status.UN_AUTHORIZED);
    };

    authenticate();
  }, []);

  // Set up Sentry user
  useEffect(() => {
    if (!user.userId) {
      return;
    }
    Sentry.setUser({ id: user.userId, email: user.email || '' });
  }, [user]);

  const updateUser = async (newData: Partial<User>, targetUserId?: string) => {
    await updateUserMutation({ userId: targetUserId || user.userId, newUserInfo: newData });
  };

  const isDesktop =
    isDesktopApp() && typeof window.lifestack?.startGoogleDesktopAuth === 'function';

  const signIn = async (provider: AuthProviderName = 'google') => {
    try {
      setStatus(Status.SIGNING_IN);

      let dbUser: User;
      let refreshToken: string;
      let accessToken: string;

      if (provider === 'apple') {
        if (isDesktop && typeof window.lifestack?.startAppleDesktopAuth === 'function') {
          const appleResult = await window.lifestack.startAppleDesktopAuth({
            clientId: process.env.REACT_APP_APPLE_CLIENT_ID || '',
            backendRedirectUri: process.env.REACT_APP_APPLE_REDIRECT_URI || '',
            scope: 'name email',
          });

          setUserId(appleResult.userId);
          safeLocalStorage.setItem('lifestack_access_token', appleResult.accessToken);
          safeLocalStorage.setItem('lifestack_refresh_token', appleResult.refreshToken);
          safeLocalStorage.setItem('lifestack_user_id', appleResult.userId);
          safeLocalStorage.setItem('jwt_configured', 'true');

          Cookies.set('lifestack_user_id', appleResult.userId, { expires: infiniteDays });
          Cookies.set('lifestack_refresh_token', appleResult.refreshToken, {
            expires: infiniteDays,
          });
          Cookies.set('lifestack_access_token', appleResult.accessToken, { expires: 30 });
          setStatus(Status.AUTHORIZED);
          trackEventMixpanel('sign_in_with_apple', {
            provider,
            userId: appleResult.userId,
          });
          return;
        }

        initiateAppleSignIn();
        return;
      }

      if (isDesktop) {
        const desktopAuth = await window.lifestack.startGoogleDesktopAuth!({
          flow: 'signin',
          scopes: DESKTOP_GOOGLE_SIGNIN_SCOPES,
        });

        const signInResult = await signInMutation({
          userId: '',
          serverAuthCode: desktopAuth.code,
          generatedClientType: 'desktop',
          redirectUri: desktopAuth.redirectUri,
          codeVerifier: desktopAuth.codeVerifier,
          skipCalendarSync: true,
          scopes: desktopAuth.scopes,
        });

        dbUser = signInResult.user;
        refreshToken = signInResult.refreshToken;
        accessToken = signInResult.accessToken;
      } else {
        const authClient = googleAuth;
        const authResult = await authClient.signinPopup();
        const authUser = authResult.profile;
        const authAccessToken = authResult.access_token;
        const authRefreshToken = authResult.refresh_token;
        const authTokenExpiryDate = authResult.expires_in
          ? transformExpiryDate(authResult.expires_in)
          : '';
        const scopes = authResult.scope ? authResult.scope.split(' ') : undefined;
        const userName =
          authUser.name ||
          authUser.given_name ||
          authUser.family_name ||
          (authUser.email ? authUser.email.split('@')[0] : undefined) ||
          '(Empty)';

        const signInResult = await signInMutation({
          userId: authUser.sub,
          accessToken: authAccessToken,
          refreshToken: authRefreshToken,
          tokenExpiryDate: authTokenExpiryDate,
          newUserInfo: {
            email: authUser.email,
            name: userName,
          },
          skipCalendarSync: true,
          scopes,
        });

        dbUser = signInResult.user;
        refreshToken = signInResult.refreshToken;
        accessToken = signInResult.accessToken;
      }

      setUserId(dbUser.userId);
      safeLocalStorage.setItem('lifestack_access_token', accessToken);
      safeLocalStorage.setItem('lifestack_refresh_token', refreshToken);
      safeLocalStorage.setItem('lifestack_user_id', dbUser.userId);
      safeLocalStorage.setItem('jwt_configured', 'true');

      Cookies.set('lifestack_user_id', dbUser.userId!, { expires: infiniteDays });
      Cookies.set('lifestack_refresh_token', refreshToken!, { expires: infiniteDays });
      Cookies.set('lifestack_access_token', accessToken!, { expires: 30 });
      setStatus(Status.AUTHORIZED);
      trackEventMixpanel('sign_in_with_google', {
        provider,
        userId: dbUser.userId,
        email: dbUser.email,
        name: dbUser.name,
      });
      return dbUser;
    } catch (error) {
      if (provider === 'google') {
        captureGoogleAuthError(error, 'sign_in', 'sign_in');
      }
      const errorMessage =
        (error as any)?.response?.data?.error ||
        (error as Error)?.message ||
        'Try again, or reach out to support if it keeps happening.';
      setStatus(Status.UN_AUTHORIZED);
      setTimeout(() => {
        setGeneralSnackbar(`Sign in/up failed. ${errorMessage}`);
      }, 2000);
    }
  };

  const signOut = async () => {
    try {
      await window.lifestack?.resetAppleDesktopAuth?.();
    } catch {
      // Best-effort native cleanup only.
    }

    setStatus(Status.UN_AUTHORIZED);
    setUserId('');
    safeLocalStorage.setItem('lifestack_user_id', '');
    safeLocalStorage.setItem('lifestack_refresh_token', '');
    safeLocalStorage.setItem('lifestack_access_token', '');
    // Keep the guided tour flag so logging out doesn't replay the tour.
    const preserved = PRESERVED_ON_SIGN_OUT_KEYS.map(
      (key) => [key, safeLocalStorage.getItem(key)] as const,
    );
    safeLocalStorage.clear();
    preserved.forEach(([key, value]) => {
      if (value !== null) safeLocalStorage.setItem(key, value);
    });
    Cookies.remove('lifestack_user_id');
    Cookies.remove('lifestack_refresh_token');
    Cookies.remove('lifestack_access_token');

    if (window.location.protocol === 'file:') {
      const currentUrl = new URL(window.location.href);
      currentUrl.hash = '/';
      window.location.replace(currentUrl.toString());
      return;
    }

    window.location.href = '/';
  };

  return (
    <AuthContext.Provider
      value={{
        status,
        isUpdatingUser,
        user,
        isUserFetching,
        signIn,
        signOut,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
